<template>
  <rmr-page-hero
    class="ctc-hero"
    title="Assistant CTC"
    title-accent="Shopping & bons plans"
    subtitle="De la fiche produit à l’article dans le CMS"
    :icon="icons.mdiShoppingOutline"
  >
    <template #actions>
      <rmr-icon-button
        :icon="icons.mdiHelpCircleOutline"
        variant="edit"
        size="sm"
        tooltip="Guide d’utilisation"
        aria-label="Guide d’utilisation"
        @click="helpOpen = true"
      />
      <rmr-segmented-control
        :value="activeView"
        :items="tabs"
        variant="on-dark"
        class="ctc-hero__tabs"
        aria-label="Navigation de l’assistant CTC"
        @input="$emit('update:activeView', $event)"
      />

      <rmr-modal
        v-model="helpOpen"
        variant="form"
        :icon="icons.mdiHelpCircleOutline"
        eyebrow="Guide"
        title="Utiliser l’assistant CTC"
        subtitle="Ce que fait chaque onglet, et ce qui est réellement créé."
        max-width="680"
      >
        <template #body>
          <v-card-text class="pa-6 ctc-help">
            <div class="ctc-help__section">
              <div class="ctc-help__title">Créer un article</div>
              <ul class="ctc-help__list">
                <li>
                  Choisissez le <strong>site</strong> : seuls les sites activés,
                  avec un média Affilizz et un connecteur CMS, sont proposés.
                </li>
                <li>
                  Collez l’URL d’un produit <strong>Amazon</strong> ou
                  <strong>Fnac</strong> puis lancez l’analyse. Les informations
                  récupérées restent modifiables.
                </li>
                <li>
                  Composez le <strong>widget comparatif</strong> ou
                  désactivez-le, puis
                  <strong>vérifiez l’affiliation Affilizz</strong> : le bouton
                  d’offre du produit principal et le widget sont créés une fois
                  sur le média du site, et leur état s’affiche séparément.
                </li>
                <li>
                  Le produit principal doit être affilié, et le widget aussi
                  s’il est inclus. Sinon, changez de produit, modifiez le widget
                  ou continuez sans widget. Une création interrompue se reprend
                  plus tard depuis « Créations à reprendre », au-dessus du
                  formulaire.
                </li>
                <li>
                  Choisissez ensuite le <strong>format</strong>, la
                  <strong>signature</strong> et la
                  <strong>publication WordPress</strong> (brouillon, publication
                  immédiate ou programmée).
                </li>
                <li>
                  « Lancer la génération » transmet l’article à la génération ;
                  WordPress le reçoit ensuite avec le statut demandé.
                </li>
              </ul>
            </div>

            <div class="ctc-help__section">
              <div class="ctc-help__title">Suivre les articles</div>
              <ul class="ctc-help__list">
                <li>
                  Liste des brouillons préparés, avec leur site et le mode de
                  publication demandé.
                </li>
                <li>
                  Les offres sont contrôlées avant la création. Un contenu
                  Affilizz est créé dès qu’Affilizz renvoie ses identifiants.
                </li>
                <li>
                  Une alerte Affilizz n’apparaît qu’en cas d’échec ou d’issue
                  incertaine. <strong>Réessayer</strong> n’est proposé que
                  lorsqu’aucun doublon n’est possible ; une création incertaine
                  se contrôle dans Affilizz.
                </li>
                <li>
                  Après « Lancer la génération », l’article est généré puis
                  envoyé à WordPress. <strong>Voir la génération AIF</strong>
                  ouvre la demande correspondante.
                </li>
              </ul>
            </div>

            <div class="ctc-help__section">
              <div class="ctc-help__title">Opportunités Affilizz</div>
              <ul class="ctc-help__list">
                <li>
                  Bons plans tendance détectés par Affilizz, synchronisés toutes
                  les deux heures.
                </li>
                <li>
                  <strong>Créer un article</strong> reprend le même parcours :
                  site, widget et vérification Affilizz, paramètres, lancement.
                  Une opportunité utilisée n’est plus proposée.
                </li>
              </ul>
            </div>

            <div class="ctc-help__section">
              <div class="ctc-help__title">Configuration</div>
              <ul class="ctc-help__list">
                <li>
                  Signature proposée par défaut pour chaque site, et activation
                  du site pour le CTC.
                </li>
                <li>
                  Indique les prérequis manquants : média Affilizz ou connecteur
                  CMS.
                </li>
              </ul>
            </div>

            <div class="ctc-help__section ctc-help__section--warning">
              <div class="ctc-help__title ctc-help__title--warning">
                Attention — publications Affilizz réelles
              </div>
              <ul class="ctc-help__list">
                <li>
                  Chaque envoi crée une
                  <strong>publication Affilizz réelle</strong>, que RMR ne peut
                  pas supprimer : elle se supprime à la main dans Affilizz.
                </li>
                <li>
                  Les publications sont nommées
                  <strong>RMR CTC / produit / ID</strong> pour être retrouvées
                  facilement.
                </li>
              </ul>
            </div>
          </v-card-text>
        </template>
      </rmr-modal>
    </template>
  </rmr-page-hero>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import {
  mdiCogOutline,
  mdiFileDocumentCheckOutline,
  mdiHelpCircleOutline,
  mdiLightbulbOnOutline,
  mdiPencilOutline,
  mdiShoppingOutline,
} from "@mdi/js";

import RmrIconButton from "@/components/ui/actions/RmrIconButton.vue";
import RmrSegmentedControl from "@/components/ui/actions/RmrSegmentedControl.vue";
import RmrModal from "@/components/ui/layout/RmrModal.vue";
import RmrPageHero from "@/components/ui/layout/RmrPageHero.vue";

import {
  VIEW_ARTICLES,
  VIEW_CONFIGURATION,
  VIEW_CREATE,
  VIEW_OPPORTUNITIES,
} from "./constants";

// Same placement as the period switch of the dashboard: the page navigation
// lives in the hero, the explanation of each tab in the guide below.
const TABS = [
  { value: VIEW_CREATE, label: "Créer un article", icon: mdiPencilOutline },
  {
    value: VIEW_ARTICLES,
    label: "Suivre les articles",
    icon: mdiFileDocumentCheckOutline,
  },
  {
    value: VIEW_OPPORTUNITIES,
    label: "Opportunités",
    title: "Opportunités Affilizz",
    icon: mdiLightbulbOnOutline,
  },
  { value: VIEW_CONFIGURATION, label: "Configuration", icon: mdiCogOutline },
];

export default {
  name: "CtcAssistantHero",
  components: { RmrIconButton, RmrModal, RmrPageHero, RmrSegmentedControl },
  props: {
    activeView: {
      type: String,
      required: true,
    },
    // The manager's Affilizz verifications waiting to be resumed.
    pendingCount: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    // The count lives in the label: read aloud, and no animation needed.
    const tabs = computed(() =>
      TABS.map((tab) => {
        if (tab.value !== VIEW_CREATE || !props.pendingCount) return tab;
        const plural = props.pendingCount > 1 ? "s" : "";
        return {
          ...tab,
          label: `${tab.label} (${props.pendingCount})`,
          title: `${props.pendingCount} création${plural} à reprendre`,
        };
      })
    );
    return {
      helpOpen: ref(false),
      icons: { mdiHelpCircleOutline, mdiShoppingOutline },
      tabs,
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-hero ::v-deep .rmr-page-hero__actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.ctc-help__section + .ctc-help__section {
  margin-top: 20px;
}

.ctc-help__section--warning {
  padding: 12px 14px;
  border: 1px solid rgba($rmr-orange, 0.3);
  border-radius: $rmr-radius-lg;
  background: rgba($rmr-orange, 0.06);
}

.ctc-help__title {
  margin-bottom: 8px;
  color: $rmr-indigo-dark;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.ctc-help__title--warning {
  color: $rmr-orange;
}

.ctc-help__list {
  margin: 0;
  padding-left: 18px;
  color: $rmr-text-secondary;
  font-size: 13px;
  line-height: 1.6;

  li + li {
    margin-top: 4px;
  }
}
</style>
